import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { useTourMode } from "../../../../contexts/TourModeContext";

export interface InstitutionalHolder {
  investorName: string;
  cik: string;
  filingDate: string;
  marketValue: number;
  sharesNumber: number;
  ownership: number;
  changeInSharesNumber: number;
  changeInSharesNumberPercentage: number;
  changeInOwnership: number;
  changeInOwnershipPercentage: number;
  holdingPeriod: number;
  firstAdded: string;
  avgPricePaid: number;
  quarterEndPrice: number;
  performancePercentage: number;
  isNew: boolean;
}

export interface InstitutionalOwnershipMetadata {
  symbol: string;
  year: number;
  quarter: number;
  quarterLabel: string;
  fallbackUsed: boolean;
}

export interface InstitutionalOwnershipResponse {
  holders: InstitutionalHolder[];
  metadata: InstitutionalOwnershipMetadata;
}

export const useInstitutionalOwnership = (symbol: string) => {
  const { isTourMode, getMockData } = useTourMode();

  return useQuery<InstitutionalOwnershipResponse, Error>({
    queryKey: ["institutionalOwnership", symbol, isTourMode],
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        const mockHolders = getMockData<InstitutionalHolder[]>("institutionalHolders");
        const mockMetadata = getMockData<InstitutionalOwnershipMetadata>("institutionalMetadata");
        if (mockHolders && mockMetadata) {
          return { holders: mockHolders, metadata: mockMetadata };
        }
      }
      const { data } = await api.get<InstitutionalOwnershipResponse>(
        "/institutional-ownership-by-holder",
        {
          params: { symbol },
        }
      );
      return data;
    },
    enabled: isTourMode || !!symbol,
    refetchOnWindowFocus: false,
    staleTime: isTourMode ? Infinity : 30 * 60 * 1000, // Never stale in tour mode
    gcTime: 60 * 60 * 1000, // 1 hour cache time
    retry: (failureCount, error) => {
      if (isTourMode) return false;
      // Don't retry on 404s - legitimately means no institutional ownership data exists
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
};
